import React, { FunctionComponent, useState } from 'react';
import { ApolloError } from 'apollo-boost';
import ReactMapGL, { Marker } from 'react-map-gl';
import { Grid, Header, Icon, Image } from 'semantic-ui-react';
import withStyles from 'react-jss';
import { pathOr, merge } from 'ramda';
import { FormattedDate } from 'react-intl';
import { Link } from 'react-router-dom';

import Query from 'components/Query';
import { VenueDetailsPlaceholder } from 'components/Placeholder';
import { ClassProp } from 'helpers/types';

import styles from './HeaderSection.styles';
import GET_VENUE_HEADER_INFO from './HeaderSection.queries';

type Result = {
  venueName: string;
  postalCode: number;
  locationCreationDate: string;
  address1: string;
  city: string;
  state: string;
  imageUrl: string;
  locationId: string;
};

type Viewport = {
  width: string | number;
  height: string | number;
  latitude: number;
  longitude: number;
  zoom: number;
};

type QueryResponse = {
  merchantList: {
    result: Result[];
  };
  loading?: boolean;
  error?: ApolloError;
};

type Props = {
  variables: { venueUuid: string };
  classes: ClassProp<typeof styles>;
};

const defaultData = {
  venueName: 'N/A',
  postalCode: 'N/A',
  address1: 'N/A',
  locationId: 'N/A',
};

const defaultViewport = {
  width: '100%',
  height: 220,
  latitude: 0,
  longitude: 0,
  zoom: 14,
};

const HeaderSection: FunctionComponent<Props> = ({ classes, variables }) => {
  const [viewportState, setViewport] = useState<Viewport>(defaultViewport);
  const updateViewport = (viewport: Viewport) => {
    setViewport(viewport);
  };

  const renderHeaderSection = (classes: any) => (response: QueryResponse) => {
    if (response.loading) {
      return <VenueDetailsPlaceholder />;
    }

    const {
      merchantName,
      offerNetworkExternalVenueId,
      venueName,
      imageUrl,
      locationCreationDate,
      postalCode,
      address1,
      city,
      state,
      locationId,
      latitude,
      longitude,
    } = pathOr(
      defaultData,
      ['data', 'searchMerchantVenues', 'result', 0],
      response,
    );

    const viewport = merge(viewportState, {
      latitude: parseFloat(latitude),
      longitude: parseFloat(longitude),
    });
    return (
      <Grid className={classes.container}>
        <Grid.Row className={classes.row}>
          <Grid.Column textAlign="left" className={classes.logo}>
            <Image
              as={Link}
              to={`/merchant/${offerNetworkExternalVenueId}/overview/${merchantName}#Sales`}
              src={imageUrl}
              className={classes.image}
            />
          </Grid.Column>
          <Grid.Column className={classes.details} width={6} textAlign="left">
            <Header as="h2" className={classes.header}>
              {venueName}
            </Header>

            <Grid>
              <Grid.Row columns={2} className={classes.info}>
                <Grid.Column className={classes.address} width={16}>
                  {address1} <br />
                  {city}, {state} - {postalCode}
                </Grid.Column>

                <Grid.Column width={6}>Dosh VUID</Grid.Column>

                <Grid.Column className={classes.value} width={10}>
                  {locationId}
                </Grid.Column>

                <Grid.Column width={6}>Live Date</Grid.Column>

                <Grid.Column
                  className={classes.value}
                  width={10}
                  textAlign="left"
                >
                  {locationCreationDate && (
                    <FormattedDate
                      value={locationCreationDate}
                      year="numeric"
                      month="short"
                      day="2-digit"
                    />
                  )}
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Grid.Column>

          <Grid.Column
            className={classes.mapContainer}
            width={8}
            floated="right"
          >
            <ReactMapGL {...viewport} onViewportChange={updateViewport}>
              <Marker longitude={longitude} latitude={latitude}>
                <Icon color="blue" name="map marker alternate" size="big" />
              </Marker>
            </ReactMapGL>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  };

  return (
    <Query
      enableProgressInfo
      variables={variables}
      query={GET_VENUE_HEADER_INFO}
      render={renderHeaderSection(classes)}
    />
  );
};

export default withStyles(styles)(HeaderSection);
